<template lang="pug">
router-link.plan-link(:to="{ name: 'PlanDetails', params: { id: plan.id } }")
  AppCard.plan-card(interactive)
    .plan
      header.plan__header
        .plan__icon(:class="variant")
          i.uil(:class="icon")
        .plan__heading
          h3.plan__name {{ planName }}
          .plan__contract \#{{ contractNumber }}
        span.plan__status(:class="variant") {{ statusLabel }}
      .plan__grid
        .plan__item
          h4 {{ $t("plan.next_invoice") }}
          span {{ nextInvoice }}
        .plan__item
          h4 {{ $t("plan.cut_off") }}
          span {{ cutOff }}
        .plan__item
          h4 {{ $t("plan.type") }}
          span {{ serviceType }}
        .plan__item
          h4 {{ $t("plan.subscription") }}
          span.plan__subscription(:class="{ 'is-on': hasSubscription }") {{ subscriptionLabel }}
        .plan__item.plan__item--wide
          h4 {{ $t("plan.bank_reference") }}
          span.plan__reference {{ bankReference }}
      .plan__address(:title="address")
        i.uil.uil-location-point(aria-hidden="true")
        span {{ address }}
      footer.plan__footer
        span {{ $t("plan.view_detail") }}
        i.uil.uil-angle-right(aria-hidden="true")
</template>

<script>
import AppCard from "@/components/AppCard";
import { computed } from "@vue/runtime-core";
import { useI18n } from "vue-i18n";
import { capitalize } from "@/utils/formatters";

const EMPTY = "—";
const ISO_DATE = /^(\d{4})-(\d{2})-(\d{2})/;

// The backend sends `false` for every field it has no value for
const isEmpty = (value) =>
  value === false || value === null || value === undefined || value === "";

export default {
  components: { AppCard },
  props: {
    plan: Object,
  },
  setup(props) {
    const { locale, t } = useI18n();

    const status = computed(() =>
      typeof props.plan.estatus === "string"
        ? props.plan.estatus.toLowerCase()
        : ""
    );

    const variant = computed(() => {
      const variants = {
        activo: "success",
        gratis: "success",
        suspendido: "warning",
        cancelado: "danger",
        inactivo: "danger",
      };

      return variants[status.value] || "neutral";
    });

    const icon = computed(() =>
      variant.value === "success" ? "uil-wifi" : "uil-wifi-slash"
    );

    const statusLabel = computed(() =>
      status.value ? capitalize(status.value) : EMPTY
    );

    const text = (value) => (isEmpty(value) ? EMPTY : String(value));

    // Odoo dates come as plain "YYYY-MM-DD" strings: build the date in local
    // time so the day never shifts, and fall back to the raw value if the
    // field turns out not to be a date at all.
    const formatDate = (value) => {
      if (isEmpty(value)) {
        return EMPTY;
      }

      const parts = ISO_DATE.exec(String(value));
      if (!parts) {
        return String(value);
      }

      const date = new Date(+parts[1], +parts[2] - 1, +parts[3]);
      return new Intl.DateTimeFormat(locale.value, {
        day: "2-digit",
        month: "short",
        year: "numeric",
      }).format(date);
    };

    const planName = computed(() => {
      const planId = props.plan.plan_id;
      return Array.isArray(planId) && planId[1] ? planId[1] : text(planId);
    });

    const contractNumber = computed(() => text(props.plan.no_contrato));
    const nextInvoice = computed(() =>
      formatDate(props.plan.fecha_proxima_factura)
    );
    const cutOff = computed(() => formatDate(props.plan.fecha_corte));
    const serviceType = computed(() =>
      isEmpty(props.plan.tipo_servicio)
        ? EMPTY
        : capitalize(String(props.plan.tipo_servicio))
    );
    const bankReference = computed(() => text(props.plan.referencia_bancaria));
    const address = computed(() => text(props.plan.domicilio_servicio));

    const hasSubscription = computed(
      () =>
        !isEmpty(props.plan.subscription_status) &&
        props.plan.subscription_status !== "false"
    );
    const subscriptionLabel = computed(() =>
      hasSubscription.value
        ? t("plan.subscription_on")
        : t("plan.subscription_off")
    );

    return {
      icon,
      variant,
      statusLabel,
      planName,
      contractNumber,
      nextInvoice,
      cutOff,
      serviceType,
      bankReference,
      address,
      hasSubscription,
      subscriptionLabel,
    };
  },
};
</script>

<style lang="scss" scoped>
.plan-link {
  @apply block;
  @apply h-full;
  @apply rounded-xl;
  @apply outline-none;

  &:focus-visible {
    @apply ring-2;
    @apply ring-gignetblue-300;
    @apply ring-offset-2;
  }

  &:hover .plan__footer span {
    @apply underline;
  }
}

.plan-card {
  @apply h-full;
}

.plan {
  @apply flex;
  @apply flex-col;
  @apply h-full;

  &__header {
    @apply flex;
    @apply items-center;
    @apply gap-3;
    @apply pb-3;
    @apply mb-3;
    @apply border-b;
    @apply border-bluegray-100;
  }

  &__icon {
    @apply flex;
    @apply flex-shrink-0;
    @apply items-center;
    @apply justify-center;
    @apply w-11;
    @apply h-11;
    @apply rounded-xl;

    i {
      @apply text-2xl;
      @apply leading-none;
    }

    &.success {
      @apply bg-gignetgreen-200;
      i {
        @apply text-gignetgreen-700;
      }
    }
    &.warning {
      @apply bg-yellow-100;
      i {
        @apply text-yellow-600;
      }
    }
    &.danger {
      @apply bg-red-100;
      i {
        @apply text-red-500;
      }
    }
    &.neutral {
      @apply bg-bluegray-100;
      i {
        @apply text-bluegray-500;
      }
    }
  }

  &__heading {
    @apply flex-1;
    @apply min-w-0;
  }

  &__name {
    @apply text-bluegray-700;
    @apply font-semibold;
    @apply leading-snug;
    @apply truncate;
  }

  &__contract {
    @apply text-xs;
    @apply text-bluegray-400;
    @apply tracking-wide;
    @apply truncate;
  }

  &__status {
    @apply flex-shrink-0;
    @apply px-2.5;
    @apply py-1;
    @apply rounded-full;
    @apply text-xs;
    @apply font-semibold;
    @apply leading-none;
    @apply whitespace-nowrap;

    &.success {
      @apply bg-gignetgreen-200;
      @apply text-gignetgreen-800;
    }
    &.warning {
      @apply bg-yellow-100;
      @apply text-yellow-700;
    }
    &.danger {
      @apply bg-red-100;
      @apply text-red-600;
    }
    &.neutral {
      @apply bg-bluegray-100;
      @apply text-bluegray-500;
    }
  }

  &__grid {
    @apply grid;
    @apply grid-cols-2;
    @apply gap-x-4;
    @apply gap-y-3;
  }

  &__item {
    @apply min-w-0;

    h4 {
      @apply text-xs;
      @apply font-medium;
      @apply text-bluegray-400;
      @apply uppercase;
      @apply tracking-wide;
      @apply truncate;
    }

    span {
      @apply block;
      @apply text-sm;
      @apply text-bluegray-700;
      @apply truncate;
    }

    &--wide {
      @apply col-span-2;
    }
  }

  &__reference {
    font-variant-numeric: tabular-nums;
  }

  // Declared after `&__item span` so it wins at the same specificity
  &__item &__subscription {
    @apply text-bluegray-400;

    &.is-on {
      @apply text-gignetgreen-700;
      @apply font-medium;
    }
  }

  &__address {
    @apply flex;
    @apply items-center;
    @apply gap-1.5;
    @apply mt-3;
    @apply text-xs;
    @apply text-bluegray-500;
    @apply min-w-0;

    i {
      @apply flex-shrink-0;
      @apply text-base;
      @apply leading-none;
      @apply text-bluegray-300;
    }

    span {
      @apply truncate;
    }
  }

  &__footer {
    @apply flex;
    @apply items-center;
    @apply justify-end;
    @apply mt-auto;
    @apply pt-4;
    @apply border-t;
    @apply border-bluegray-100;
    @apply text-sm;
    @apply font-medium;
    @apply text-gignetblue-400;

    i {
      @apply text-xl;
      @apply leading-none;
    }
  }
}
</style>
